import {
  FC,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import Table from "apps/website/components/feature/Table/Table";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import SummaryFixedPriceRow from "apps/website/components/form/Summary/SummaryFixedPrice/SummaryFixedPriceRow";
import {
  getFieldValues,
  getFlowFieldValuesForAction,
  getFlowProduct,
} from "@./state";
import { IPlanSummaryResponse, ISummaryItem } from "@forms/schema";
import Column from "apps/website/components/layout/Column/Column";
import useFormsServiceStore
  from "libs/state/src/lib/stores/useFormServiceStore";
import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "../../../../utils/tracking/getActionTrackingInfo";
import { useAPI } from "../../../../hooks/useAPI";
import { usePostHog } from "posthog-js/react";
import { useDatadog } from "@auth/client-sdk-react";
import { useTrackPageView } from "../../../../contexts/tracking";
import { useCookies } from "react-cookie";

interface FixedPriceSummaryProps {
  image?: IImageProps;
  flowSlug: string;
  trialTitle?: string;
  pricePerActiveCat?: number;
  mealsPerPlan?: number;
  feedsForDays?: number;
}

export const FixedPriceSummary: FC<FixedPriceSummaryProps> = ({
  image,
  flowSlug,
  trialTitle = "In your Fresh Feast Trial:",
  pricePerActiveCat = 20,
  mealsPerPlan = 28,
  feedsForDays = 14,
}) => {
  const [ flowFieldValues, setFlowFieldValue ] = useFormsServiceStore(
    (x) => ([ x.flows.find((f) => f.id === flowSlug), x.setFlowFieldValue ]),
  );
  const [ summaryData, setSummaryData ] = useState<IPlanSummaryResponse | undefined>(undefined);
  const formsActionsAPI = useAPI().FormActions;
  const posthog = usePostHog();
  const datadog = useDatadog();
  const tracking = useTrackPageView();
  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);
  const [ refreshKey, setRefreshKey ] = useState(0);

  const catNames = useMemo(() => getFieldValues(flowSlug, "catName"), [ flowSlug, refreshKey ]);
  const removedStatus = useMemo(() => getFieldValues(flowSlug, "catPlanRemoved"), [ flowSlug, refreshKey ]);
  const catAllergies = useMemo(() => getFieldValues(flowSlug, "catAllergies"), [ flowSlug, refreshKey ]);

  const items: ISummaryItem[] = useMemo(() => {
    // Calculate number of non-removed cats
    const activeCatsCount = catNames.filter((cat) => !removedStatus
      .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue)).length;

    const isLastCat = activeCatsCount === 1;

    const catItems: ISummaryItem[] = catNames.map((cn) => {
      const planRemoved = !!removedStatus.find((x) => x.key.linkingId === cn.key.linkingId)?.data.submitValue || false;
      const catDisplayName = typeof cn.data.displayValue === "string" ? cn.data.displayValue : "";

      return {
        title: planRemoved ? undefined : { value: `${mealsPerPlan} meals, feeds ${catDisplayName} for ${feedsForDays} days` },
        // Only include removalData if it's not the last cat OR if the cat is already removed
        removalData: (isLastCat && !planRemoved) ? undefined : {
          removeTitle: `Remove ${catDisplayName}'s plan`,
          addTitle: `Add back ${catDisplayName}'s plan`,
          linkingId: cn.key.linkingId,
          planRemoved,
          catName: catDisplayName,
        },
      };
    });

    const catsWithoutChickenAllergy = catNames.filter((cat) => {
      // Check if cat plan is removed
      const isCatRemoved = removedStatus
        .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue);

      if (isCatRemoved) {
        return false;
      }

      // Check if cat has chicken allergy
      const hasChickenAllergy = catAllergies.find((allergy) => (
        allergy.key.linkingId === cat.key.linkingId &&
        Array.isArray(allergy.data.submitValue) ? allergy.data.submitValue?.includes("CHICKEN")
          : allergy.data.submitValue === "CHICKEN"
      ));

      return !hasChickenAllergy;
    }).length;

    const baseItems = [
      ...catItems,
      {
        title: { value: "Personalised switching guide" },
      },
      {
        title: { value: "Free delivery" },
      },
      {
        title: { value: "Support from our in-house Vet Nurse" },
      },
    ];

    if (catsWithoutChickenAllergy > 0) {
      baseItems.splice(catItems.length, 0, {
        title: { value: `Free Chicken Sprinkles meal topper (x${catsWithoutChickenAllergy})` },
      });
    }

    return baseItems;
  }, [ catNames, removedStatus, catAllergies, mealsPerPlan, feedsForDays ]);

  const totalPrice = useMemo(() => {
    const activeCats = catNames.filter((cat) => !removedStatus
      .find((status) => status.key.linkingId === cat.key.linkingId && status.data.submitValue));
    return activeCats.length * pricePerActiveCat;
  }, [ catNames, removedStatus, pricePerActiveCat ]);

  const reloadSummary = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const getSummaryData = useCallback(
    async (): Promise<IPlanSummaryResponse | undefined> => {
      const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowSlug);
      const featureFlags = posthog.featureFlags.getFlagVariants();

      try {
        if (fieldSubmitValuesMap.has("freshDeliveryDate") || fieldSubmitValuesMap.has("deliveryDate")) {
          const result = await formsActionsAPI
            .performAction<IPlanSummaryResponse>(
              "freshPlanSummaryNewCustomer",
              fieldSubmitValuesMap,
              getFlowProduct(flowSlug),
              flowSlug,
              undefined,
              featureFlags,
              getActionTrackingInfo(cookies),
              datadog.logger,
            );
          if (result.success) {
            const planSummaryResponse = result.responseBody;
            return planSummaryResponse;
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [ flowSlug, formsActionsAPI, flowFieldValues, tracking ],
  );

  const loadSummary = useCallback(async () => {
    try {
      const result = await getSummaryData();

      setSummaryData(result);
    } catch (error) {
      console.error("Error loading summary data:", error);
    }
  }, [ getSummaryData ]);

  useEffect(() => {
    void loadSummary();
  }, [ loadSummary, refreshKey ]);

  useEffect(() => {
    if (summaryData) {
      if (summaryData.costTodayRaw !== undefined) {
        setFlowFieldValue(flowSlug, "costTodayRaw", {
          submitValue: summaryData.costTodayRaw, displayValue: summaryData.costTodayRaw,
        });
      }
      if (summaryData.costPerDay !== undefined) {
        setFlowFieldValue(flowSlug, "costPricePerDay", {
          submitValue: summaryData.costPerDay, displayValue: summaryData.costPerDay,
        });
      }

      if (summaryData.costRecurring !== undefined) {
        setFlowFieldValue(flowSlug, "costRecurring", {
          submitValue: summaryData.costRecurring, displayValue: summaryData.costRecurring,
        });
      }
      if (summaryData.multicatDiscount !== undefined) {
        if (summaryData.multicatDiscount.preDiscountCostPerDay !== undefined) {
          setFlowFieldValue(flowSlug, "preMulticatDiscountCostPerDay", {
            submitValue: summaryData.multicatDiscount.preDiscountCostPerDay,
            displayValue: summaryData.multicatDiscount.preDiscountCostPerDay,
          });
        }
        if (summaryData.multicatDiscount.preDiscountCostRecurring !== undefined) {
          setFlowFieldValue(flowSlug, "preMulticatDiscountCostRecurring", {
            submitValue: summaryData.multicatDiscount.preDiscountCostRecurring,
            displayValue: summaryData.multicatDiscount.preDiscountCostRecurring,
          });
        }
      }
    }
  }, [ summaryData, flowSlug, setFlowFieldValue ]);

  return (
    <Column>
      <div data-component={ FixedPriceSummary.name }>
        { image?.src && (
          <Image image={ image } alt={ image.alt ?? trialTitle } />
        ) }
        <Table className={ "w-full border-collapse bg-white" }>
          <tr>
            <th
              className={ `text-left px-2 py-2 ${themeRootClassMap.dark} text-white` }
              data-theme="dark"
              scope="col"
              colSpan={3}
            >
              <div className="flex items-center justify-between">
                <TextSubtitle size={2}>{ trialTitle }</TextSubtitle>
              </div>
            </th>
          </tr>
          <>
            <>

              <>
                { items?.map(((item, index) => (
                  <SummaryFixedPriceRow
                    key={ index }
                    item={ item }
                    className="bg-white"
                    reloadSummary={ reloadSummary }
                    flowSlug={ flowSlug }
                  />
                ))) }
              </>
              <>
                <tr className="bg-white">
                  <td colSpan={3} className="px-2">
                    <div className="bg-light-grey px-2 py-2">
                      <div className="flex justify-between items-center">
                        <TextSubtitle size={2}>Total today</TextSubtitle>
                        <TextSubtitle size={2}>£{ totalPrice }</TextSubtitle>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="bg-white">
                  <td colSpan={3} className="px-2">
                    <div className="px-2 py-2">
                      <div className="flex justify-between items-center">
                        <TextSubtitle size={4}>Price per meal</TextSubtitle>
                        <TextSubtitle size={4}>£0.71</TextSubtitle>
                      </div>
                    </div>
                  </td>
                </tr>
              </>
            </>
          </>
        </Table>
      </div>
    </Column>
  );
};
